/**
 * Notification System Types
 * Types for the database-persisted notification system
 */

// Notification priority levels (matches database enum)
export type NotificationPriority = 'urgent' | 'action_required' | 'info';

// Notification categories (matches database enum, maps to dashboard tabs/features)
export type NotificationCategory =
  // Business Dashboard
  | 'reviews'
  | 'loyalty'
  | 'coupons'
  | 'customers'
  | 'email'
  | 'social'
  | 'signage'
  // Employee Dashboard
  | 'shifts'
  | 'time_tracking'
  | 'payroll'
  | 'employees'
  | 'communications'
  // Franchise
  | 'franchise'
  // System
  | 'ai_chat'
  | 'system'
  | 'subscription';

// Main notification interface (matches database schema)
export interface Notification {
  id: string;
  business_id: string | null;
  user_id: string | null;
  category: NotificationCategory;
  priority: NotificationPriority;
  title: string;
  message: string | null;
  action_url: string | null;
  action_label: string | null;
  source_type: string | null;
  source_id: string | null;
  read_at: string | null;
  dismissed_at: string | null;
  expires_at: string | null;
  created_at: string;
}

// Notification for creating (without server-generated fields)
export interface CreateNotificationInput {
  business_id?: string;
  user_id?: string;
  category: NotificationCategory;
  priority?: NotificationPriority;
  title: string;
  message?: string;
  action_url?: string;
  action_label?: string;
  source_type?: string;
  source_id?: string;
  expires_at?: string;
}

// Notification counts by category
export type NotificationCountByCategory = Partial<Record<NotificationCategory, number>>;

// Notification counts by priority
export interface NotificationCountByPriority {
  urgent: number;
  action_required: number;
  info: number;
}

// Filter options for notifications
export type NotificationFilterType = 'all' | NotificationPriority | 'system';

export interface NotificationFilters {
  filter: NotificationFilterType;
  categories?: NotificationCategory[];
}

// System notification (from toast messages)
export interface SystemNotification {
  id: string;
  title: string;
  description?: string;
  type: 'success' | 'error' | 'warning' | 'info' | 'default';
  timestamp: number;
  category?: string;
  businessName?: string;
}

// Notification display configuration
export interface NotificationDisplayConfig {
  icon: string;
  color: string;
  bgColor: string;
  borderColor: string;
}

// Category display configuration
export interface CategoryDisplayConfig {
  icon: string;
  label: string;
  color: string;
}

// Priority display configuration
export const PRIORITY_CONFIG: Record<NotificationPriority, NotificationDisplayConfig> = {
  urgent: {
    icon: 'AlertTriangle',
    color: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-50 dark:bg-red-900/20',
    borderColor: 'border-red-200 dark:border-red-800',
  },
  action_required: {
    icon: 'AlertCircle',
    color: 'text-amber-600 dark:text-amber-400',
    bgColor: 'bg-amber-50 dark:bg-amber-900/20',
    borderColor: 'border-amber-200 dark:border-amber-800',
  },
  info: {
    icon: 'Info',
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-50 dark:bg-blue-900/20',
    borderColor: 'border-blue-200 dark:border-blue-800',
  },
};

// Category display configuration
export const CATEGORY_CONFIG: Record<NotificationCategory, CategoryDisplayConfig> = {
  // Business Dashboard
  reviews: { icon: 'Star', label: 'Reviews', color: 'text-yellow-500' },
  loyalty: { icon: 'Heart', label: 'Loyalty', color: 'text-pink-500' },
  coupons: { icon: 'Ticket', label: 'Coupons', color: 'text-purple-500' },
  customers: { icon: 'Users', label: 'Customers', color: 'text-cyan-500' },
  email: { icon: 'Mail', label: 'Email', color: 'text-blue-500' },
  social: { icon: 'Share2', label: 'Social', color: 'text-indigo-500' },
  signage: { icon: 'Monitor', label: 'Signage', color: 'text-slate-500' },
  // Employee Dashboard
  shifts: { icon: 'Calendar', label: 'Shifts', color: 'text-green-500' },
  time_tracking: { icon: 'Clock', label: 'Time Tracking', color: 'text-teal-500' },
  payroll: { icon: 'DollarSign', label: 'Payroll', color: 'text-emerald-500' },
  employees: { icon: 'UserCheck', label: 'Employees', color: 'text-violet-500' },
  communications: { icon: 'MessageSquare', label: 'Messages', color: 'text-sky-500' },
  // Franchise
  franchise: { icon: 'Building2', label: 'Franchise', color: 'text-orange-500' },
  // System
  ai_chat: { icon: 'Bot', label: 'AI Assistant', color: 'text-primary' },
  system: { icon: 'Settings', label: 'System', color: 'text-gray-500' },
  subscription: { icon: 'CreditCard', label: 'Subscription', color: 'text-rose-500' },
};

// Hook return types
export interface UseNotificationsReturn {
  notifications: Notification[];
  isLoading: boolean;
  error: Error | null;
  unreadCount: number;
  countByCategory: NotificationCountByCategory;
  countByPriority: NotificationCountByPriority;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  dismiss: (id: string) => Promise<void>;
  dismissAll: () => Promise<void>;
  refetch: () => void;
}

// Context value type
export interface NotificationContextValue extends UseNotificationsReturn {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  filter: NotificationFilterType;
  setFilter: (filter: NotificationFilterType) => void;
  // System notifications from toast messages
  systemNotifications: SystemNotification[];
  systemNotificationCount: number;
  clearSystemNotifications: () => void;
}
